import { CellValueChangedEvent, GridReadyEvent } from '@ag-grid-community/core';
import { Component, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { EntityIssue, JoDetail, JoHeader, JoNote, JoNoteType, PoDetail, PoHeader, PoNote, PoNoteType, WarningTypeEnum } from 'app/model/entities/entity-model';
import { JoNoteTypeCode } from 'app/model/enums/jo-note-type-code';
import { PoNoteTypeCode } from 'app/model/enums/po-note-type-code';
import { AgFns, GridOptionsExt } from 'app/shared/ag-fns';
import { UtilFns } from 'app/shared/util-fns';
import { Entity, EntityState } from 'breeze-client';
import _ from 'lodash';
import {
    DynamicDialogConfig,
    DynamicDialogRef
} from 'primeng/dynamicdialog';

import { DialogBaseComponent, DialogParams } from './dialog-base.component';
import { DomainService } from './domain.service';

export interface EditNotesDialogParams extends DialogParams {
  xHeader?: JoHeader | PoHeader;
  xDetail?: JoDetail | PoDetail;
}

// Note: this dialog assumes that if we are editing/creating a poDetail note
// that the joDetail and joHeader corresponding to this poDetail has been fetched
@Component({
    templateUrl: './edit-notes-dialog.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class EditNotesDialogComponent  extends DialogBaseComponent<EditNotesDialogParams> {
  @ViewChild('buttonsCell') buttonsCell: TemplateRef<any>;

  typeInit: 'P' | 'J';
  header: JoHeader | PoHeader;
  detail: JoDetail | PoDetail;
  notes: JoNote[] | PoNote[];
  accountId: number;
  isEditingAllowed = false;


  notesGridOptions: GridOptionsExt;
  userInit: string;

  isPageReady = false;
  noteTypeMap: object;

  constructor(
    public config: DynamicDialogConfig<EditNotesDialogParams>,
    public dialogRef: DynamicDialogRef,
    public domainService: DomainService
  ) {
    super(config, dialogRef, domainService);
    Object.assign(this.config, {
      width: '80vw',
      height: '56vh'
    });


    this.header = this.params.xHeader;
    this.detail = this.params.xDetail;
    this.userInit = this.authService.getUser().initials;
    if (this.header != null) {
      this.typeInit = this.header.entityType.shortName === 'PoHeader' ? 'P' : 'J';
    } else {
      this.typeInit = this.detail.entityType.shortName === 'PoDetail' ? 'P' : 'J';
    }
    this.prepare();
  }

  async prepare() {

    this.canEditCell = this.canEditCell.bind(this);
    this.isCellDisabled = this.isCellDisabled.bind(this);

    this.notesGridOptions = AgFns.initGridOptions( this, {
      onGridReady: this.onNotesGridReady,
      onCellValueChanged: this.onCellValueChanged,
      onModelUpdated: (p) => {} // to suppress automatic autosize
    });

    let noteTypes: JoNoteType[] | PoNoteType[];
    let typeDescr: string;
    if (this.typeInit === 'P') {
      typeDescr = 'Purchase Order';
      noteTypes = await this.dbQueryService.cacheAndGetAll(PoNoteType);
      if (this.detail == null) {
        this.notes = await this.dbQueryService.getPoNotesForHeader(this.header.id);
        this.accountId = null;
      } else {
        this.notes = await this.dbQueryService.getPoNotesForDetail(this.detail.id);
        // Assumes that both JoDetail and JoHeader have also been retrieved.
        const poDetail = <PoDetail> this.detail;
        if (poDetail.joDetail != null) {
          this.accountId = poDetail.joDetail.joHeader.accountId;
        } else {
          // this can happen for Stock and Special orders
          this.accountId = null;
        }
      }
    } else {
      typeDescr = 'Job Order';
      noteTypes = await this.dbQueryService.cacheAndGetAll(JoNoteType);
      if (this.detail == null) {
        this.notes = await this.dbQueryService.getJoNotesForHeader(this.header.id);
        this.accountId = (<JoHeader> this.header).accountId;
      } else {
        this.notes = await this.dbQueryService.getJoNotesForDetail(this.detail.id);
        this.accountId = (<JoDetail> this.detail).joHeader.accountId;
      }
    }
    this.noteTypeMap = UtilFns.objectArrayToObject(noteTypes, 'id', 'name');

    let title;
    if (this.detail == null) {
      this.header = this.config.data.xHeader;
      title = `Notes for ${typeDescr}: ${ this.header.id }`;
    } else {
      this.header = this.detail.getHeader();
      title = `Notes for ${typeDescr} Detail: ${ this.detail.id }`;
    }

    // this.setTitle(this.title);
    this.config.header = title;

    this.isPageReady = true;
  }


  onNotesGridReady(event: GridReadyEvent) {
    let fldNoteTypeId: string;
    let fldDetailId: string;
    let title: string;
    if (this.typeInit === 'P') {
      title = 'P/O Detail';
      fldNoteTypeId = 'poNoteTypeId';
      fldDetailId = 'poDetailId';
    } else {
      fldNoteTypeId = 'joNoteTypeId';
      fldDetailId = 'joDetailId';
    }
    const colDefs = [
      { ...AgFns.createCellButtonProps('', this.buttonsCell, 'del'), maxWidth: 40 },
      { headerName: 'Note Type', field: fldNoteTypeId, editable: false, width: 240,
        ...AgFns.createDropdownEditorPropsFromMapObj(this.noteTypeMap),
      },
      { headerName: 'Note', field: 'note', flex: 1, minWidth: 400,
        cellEditor: 'agLargeTextCellEditor',  cellEditorParams: { maxLength: 1024 },
        editable: this.canEditCell, tooltipField: 'note',
        autoHeight: true, wrapText: true  },
      { ...AgFns.createCheckboxProps('Is Issue', 'isIssue'),
        editable: this.canEditCell, maxWidth: 90,
      },
      { headerName: 'Created', field: 'crtnTs', width: 120 },
      { headerName: 'User', field: 'crtnUserInit', width: 100 },
      { headerName: title, field: fldDetailId, width: 90 },
      { headerName: 'Modified', field: 'modTs', width: 120 },
      { headerName: 'User', field: 'modUserInit', width: 100 },
    ];
    const sortModel = [{ colId: 'crtnTs', sort: 'desc' as const }];
    AgFns.initGrid(event, colDefs, sortModel);
    this.updateColumnVisibility();

  }

  onCellValueChanged(e: CellValueChangedEvent) {
    if (e.colDef.field === 'isIssue') {
      this.notesGridOptions.apiX.redrawRows();
    }
  }

  addNote() {
    this.notesGridOptions.apiX.stopEditing();
    if (this.typeInit === 'P') {
      this.createPoNote();
    } else {
      this.createJoNote();
    }

    this.refreshUI();
    this.notesGridOptions.apiX.setFocusedCell(0, 'note');
  }

  allowEditing() {
    if (this.authService.getUser().isUserAdmin) {
      this.isEditingAllowed = true;
      this.updateColumnVisibility();
    } else {
      this.dialogService.showOkMessage('Insufficient Access Rights', 'Only users with "userAdmin" rights can edit notes');
    }
  }

  disallowEditing() {
    this.isEditingAllowed = false;
    this.updateColumnVisibility();
  }

  updateColumnVisibility() {
    this.notesGridOptions.apiX.setColumnsVisible(['del'], this.isEditingAllowed);
    this.notesGridOptions.apiX.setColumnWidths( [ { key: 'del', newWidth:  40 } ]);
    this.notesGridOptions.apiX.refreshCells( { force: true });
  }

  createPoNote() {
    const now = new Date();
    const struct = <PoNote> {
      poNoteTypeId: PoNoteTypeCode.Other,
      crtnTs: now,
      crtnUserInit: this.userInit,
      modTs: now,
      modUserInit: this.userInit,
      note: '',
    };
    const poDetail = this.detail as PoDetail;
    if (poDetail == null) {
      struct.poHeaderId = this.header.id;
    } else {
      struct.poDetailId = poDetail.id;
      // a PoDetail can be missing PoHeader if not yet imported.cr
      struct.poHeaderId = poDetail.poHeader?.id;
    }
    this.uow.createEntity(PoNote, struct);
  }

  createJoNote() {
    const now = new Date();
    const struct = <JoNote> {
      joNoteTypeId: JoNoteTypeCode.Other,
      crtnTs: new Date(),
      crtnUserInit: this.userInit,
      modTs: now,
      modUserInit: this.userInit,
      note: '',
    };
    if (this.detail == null) {
      struct.joHeaderId = this.header.id;
    } else {
      struct.joDetailId = this.detail.id;
      struct.joHeaderId = (this.detail as JoDetail).joHeader.id;
    }
    this.uow.createEntity(JoNote, struct);
  }

  deleteNote(note: JoNote | PoNote) {
    // 2026-05-25 / JM — System-generated JoNotes (joNoteTypeId !== 'Other')
    // are immutable audit-trail entries written by the server interceptor;
    // not even admins can delete them.
    if (this.isSystemGeneratedNote(note)) {
      this.dialogService.showOkMessage(
        'Cannot Delete System Note',
        'System-generated notes are part of the audit trail and cannot be deleted.',
      );
      return;
    }
    if (this.authService.getUser().isUserAdmin) {
      note.entityAspect.setDeleted();
      this.refreshUI();
    } else {
      this.dialogService.showOkMessage('Insufficient Access Rights', 'Only users with "userAdmin" rights can delete notes');
    }
  }

  private isSystemGeneratedNote(note: JoNote | PoNote): boolean {
    // PoNotes are out of scope for the audit-trail project; only JoNotes
    // other than 'Other' are system-generated.
    if ((note as any).joNoteTypeId == null) return false;
    return (note as JoNote).joNoteTypeId !== JoNoteTypeCode.Other;
  }

  async save() {
    this.notesGridOptions.apiX.stopEditing();
    // Don't bother saving 'empty' notes.
    let notes = this.getSaveableNotes();
    notes = notes.slice().filter((note: PoNote | JoNote) => {
      if (note.note.trim().length === 0) {
        note.entityAspect.rejectChanges();
        return false;
      }
      return true;
    });

    const addedNotes = notes.filter(n => n.entityAspect.entityState.isAdded());
    // await this.updateAddedNotes(addedNotes);
    const modifiedNotes = notes.filter(n => n.entityAspect.entityState.isModified());
    await this.updateModifiedNotes(modifiedNotes);

    await this.addUpdateEntityIssues(notes);

    this.dbSaveService.saveChanges();

    this.dialogRef.close(null);
  }

  // Removed per discussion with Jeff on 4/18/2022
  // private async updateAddedNotes(notes: (JoNote | PoNote)[]) {
  //   // If this is a PoDetail note and references a JoDetail then copy this note to
  //   // a new JoDetail note
  //   const poNotes = <PoNote[]>notes.filter(n => n.entityType.shortName === 'PoNote');
  //   const poDetailNotes = poNotes.filter(n => n.poDetailId != null && n.poDetail.joDetailId != null);
  //   poDetailNotes.forEach(pon => {
  //     const struct = <JoNote>{
  //       joNoteTypeId: JoNoteTypeCode.PodNote,
  //       crtnTs: pon.crtnTs,
  //       crtnUserInit: this.userInit,
  //       modTs: pon.crtnTs,
  //       modUserInit: this.userInit,
  //       note: '',
  //     };
  //     struct.joDetailId = pon.poDetail.joDetailId;
  //     // Note: next line assumes that the joDetail corresponding to this joDetail.note has been fetched
  //     struct.joHeaderId = pon.poDetail.joDetail.joHeaderId;
  //     struct.note = 'Copied from P/O Detail note: ' + pon.note; // copy the poDetail note
  //     this.uow.createEntity(JoNote, struct);
  //   });

  // }

  private async updateModifiedNotes(notes: (JoNote | PoNote)[]) {
    notes.forEach(n => {
      n.modTs = new Date();
      n.modUserInit = this.userInit;
    });

  }

  async addUpdateEntityIssues(notes: (PoNote | JoNote)[]) {
    let issueNotes = notes.filter(n => n.isIssue && n.entityAspect.entityState.isAdded());
    issueNotes.forEach(n => this.createIssue(n));
    issueNotes = notes.filter(n => n.entityAspect.entityState.isModified());
    const promises = issueNotes.map(async n => {
      const issue = await this.getIssue(n);
      if (issue == null && n.isIssue) {
        this.createIssue(n);
      } else if ( issue != null && !n.isIssue) {
        issue.entityAspect.setDeleted();
      } else if ( issue != null && n.note !== issue.description ) {
        issue.description = n.note;
      }
      return issue;
    });
    await Promise.all(promises);
  }

  createIssue(note: JoNote | PoNote) {
    const issueStruct = <EntityIssue> {};
    issueStruct.crtnTs = note.crtnTs;
    issueStruct.accountId = this.accountId;
    issueStruct.crtnUserInit = note.crtnUserInit;
    issueStruct.description = note.note;
    issueStruct.warningTypeId = WarningTypeEnum.Warning;
    const ei = this.getEntityInfo(note);
    issueStruct.entityTypeName = ei.entityTypeName;
    issueStruct.entityId =  ei.entityId;
    const issue = this.uow.createEntity(EntityIssue, issueStruct);
  }

  async getIssue(note: PoNote | JoNote) {
    const ei = this.getEntityInfo(note);
    let  issues = await this.dbQueryService.getEntityIssues(ei.entityTypeName, ei.entityId);
    // cannot compare javascript dates with ===
    issues = issues.filter(iss => iss.crtnTs.getTime() === note.crtnTs.getTime());
    return issues.length ? issues[0] : null;
  }

  getEntityInfo(note: PoNote | JoNote) {
    const headerId = note['joHeaderId'] || note['poHeaderId'];
    const detailId = note['joDetailId'] || note['poDetailId'];
    const prefix = note.entityType.shortName.slice(0, 2); // Jo or Po
    const suffix =  detailId != null ? 'Detail' : 'Header';
    const entityTypeName = prefix + suffix;
    const entityId =  (detailId != null ? detailId : headerId).toString();
    return { entityTypeName: entityTypeName, entityId: entityId};
  }

  undo(shouldExit: boolean) {
    this.notesGridOptions.apiX.stopEditing();
    const notes = this.getSaveableNotes(true);

    notes.slice().forEach(note => note.entityAspect.rejectChanges());
    if (shouldExit) {
      this.dialogRef.close(null);
    } else {
      this.refreshUI();
    }
  }

  private getSaveableNotes(includeDeleted = false) {
    let typeName: string;
    if (this.typeInit === 'P') {
      typeName = 'PoNote';
    } else {
      typeName = 'JoNote';
    }
    this.notesGridOptions.apiX.stopEditing();
    const entityStates = [EntityState.Added, EntityState.Modified];
    if (includeDeleted) {
      entityStates.push(EntityState.Deleted);
    }
    const notes = <(PoNote | JoNote)[]> this.uow.manager.getEntities(typeName, entityStates );
    return notes;
  }

  canEditEntity(entity: Entity) {
    // 2026-05-25 / JM — System-generated JoNotes are read-only forever.
    if (this.isSystemGeneratedNote(entity as JoNote | PoNote)) {
      return false;
    }
    const es = entity.entityAspect.entityState;
    if (es.isAdded()) {
      return true;
    }
    if (es.isUnchangedOrModified()) {
      return this.isEditingAllowed;
    }
  }

  canEditCell(params) {
    const entity = <Entity> params.node.data;
    return this.canEditEntity(entity);
  }

  isCellDisabled(data: JoNote | PoNote) {
    return !this.canEditEntity(data);
  }

  isRowDisabled(entity: Entity) {
    return !entity.entityAspect.entityState.isAdded();
  }

  private refreshUI() {
    let notes = <(JoNote | PoNote)[]> (this.detail != null ? this.detail.getNotes() : this.header.getNotes());
    notes = _.orderBy(notes, n => n.crtnTs, ['desc']);
    this.notesGridOptions.apiX.setGridOption('rowData', notes);
  }
}
